// Polyfill required for Safari 13 and below
import '@formatjs/intl-locale/polyfill';
import '@formatjs/intl-numberformat/polyfill';
import '@formatjs/intl-numberformat/locale-data/en';
import '@formatjs/intl-numberformat/locale-data/fr';
import '@formatjs/intl-numberformat/locale-data/ru';

import { Locale as DateFNSLocale } from 'date-fns';
import enDateFnsLocale from 'date-fns/locale/en-GB';
import usDateFnsLocale from 'date-fns/locale/en-US';
import frDateFnsLocale from 'date-fns/locale/fr';
import ruDateFnsLocale from 'date-fns/locale/ru';
import React, { useMemo } from 'react';
import { IntlProvider as ReactIntlProvider } from 'react-intl';
import { useSelector } from 'react-redux';

import { getCountryCode } from '../modules/common/store/selectors';
import { errorHandling } from '../services/errorHandling';
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE, TLanguageTag } from '../services/userLanguage';
import { CountryCode, TCountryCodes } from '../shared/models/country-code';

import messagesEn from './compiled-lang/en.json';
import messagesFr from './compiled-lang/fr.json';
import messagesRu from './compiled-lang/ru.json';
import { useUserLanguage } from './useUserLanguage';

interface TIntlContext {
  setLanguage: (lang: TLanguageTag) => void;
  availableLanguages: TLanguageTag[];
  language: TLanguageTag;
  region: TCountryCodes;
  dateFnsLocale: DateFNSLocale;
}

const IntlContext = React.createContext<TIntlContext>({
  setLanguage: () => {
    errorHandling.captureMessage(
      "Something went wrong with locale context. Looks like you're trying to switch locale before app has completely loaded",
    );
  },
  availableLanguages: AVAILABLE_LANGUAGES,
  language: DEFAULT_LANGUAGE,
  region: CountryCode.Unknown,
  dateFnsLocale: enDateFnsLocale,
});

const getDateFnsLocale = (language: TLanguageTag, region: TCountryCodes): DateFNSLocale => {
  if (language === 'en' && region === CountryCode.UnitedStates) {
    return usDateFnsLocale;
  }

  switch (language) {
    case 'fr':
      return frDateFnsLocale;
    case 'ru':
      return ruDateFnsLocale;
    default:
      return enDateFnsLocale;
  }
};

const getLocale = (language: TLanguageTag, region: TCountryCodes): string => {
  if (language !== 'en') {
    return language;
  }

  return region === CountryCode.UnitedStates ? 'en-US' : 'en-GB';
};

const messagesMap: Record<TLanguageTag, any> = {
  en: messagesEn,
  ru: messagesRu,
  fr: messagesFr,
};

type Props = {
  children?: React.ReactNode;
};

const IntlProvider = ({ children }: Props) => {
  const { language, setLanguage } = useUserLanguage();
  const region = useSelector(getCountryCode);

  const contextValue = useMemo(
    () => ({
      setLanguage,
      availableLanguages: AVAILABLE_LANGUAGES,
      language,
      region,
      dateFnsLocale: getDateFnsLocale(language, region),
    }),
    [setLanguage, language, region],
  );

  const locale = getLocale(language, region);

  return (
    <IntlContext.Provider value={contextValue}>
      <ReactIntlProvider
        key={locale}
        locale={locale}
        messages={messagesMap[language]}
        defaultLocale={getLocale(DEFAULT_LANGUAGE, region)}
      >
        {children}
      </ReactIntlProvider>
    </IntlContext.Provider>
  );
};

export { IntlProvider, IntlContext };
